import { useEffect, useState } from "react";
import http from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Trash2, CalendarX, Pencil } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";

const EMPTY = { start_date: "", end_date: "", all_day: true, start_time: "", end_time: "", reason: "", notice_ru: "", notice_no: "", show_on_website: false, notify_ai: false };

export default function AdminSchedule() {
  const [periods, setPeriods] = useState([]);
  const [staff, setStaff] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [translating, setTranslating] = useState(false);
  const translate = async () => {
    setTranslating(true);
    try { const r = await http.post("/v1/admin/translate", { text: form.notice_ru, source: "ru", target: "no" }); setForm((f) => ({ ...f, notice_no: r.data.translation })); }
    catch { toast.error("Перевод не удался"); } finally { setTranslating(false); }
  };

  const load = async () => {
    try {
      const [p, s] = await Promise.all([
        http.get("/v1/admin/blocked-periods"),
        http.get("/v1/admin/staff"),
      ]);
      setPeriods(p.data);
      setStaff(s.data);
    } catch {
      toast.error("Не удалось загрузить данные");
    }
  };
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!form.start_date || !form.end_date) { toast.error("Укажите даты"); return; }
    if (!form.all_day && (!form.start_time || !form.end_time)) { toast.error("Укажите время"); return; }
    if ((form.show_on_website || form.notify_ai) && !form.notice_no.trim()) { toast.error("Для публикации нужен текст объявления на норвежском"); return; }
    setSaving(true);
    try {
      const body = { ...form, end_date: form.end_date || form.start_date };
      if (editId) await http.put(`/v1/admin/blocked-periods/${editId}`, body); else await http.post("/v1/admin/blocked-periods", body);
      toast.success(editId ? "Сохранено" : "Период блокировки добавлен");
      setForm(EMPTY); setEditId(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Ошибка");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Удалить этот блокированный период? Дата снова станет доступной для записи.")) return;
    try {
      await http.delete(`/v1/admin/blocked-periods/${id}`);
      toast.success("Удалено");
      load();
    } catch {
      toast.error("Ошибка");
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-schedule">
      <div>
        <h1 className="text-2xl font-bold text-white">Доступность и блокировки</h1>
        <p className="text-slate-400 text-sm mt-1">Рабочие часы задаются в «Цены и часы» (последний слот — через 30 мин после закрытия, по договорённости). Выходные закрыты. Блокировки (отпуск, больничный, исключения) недоступны для записи на сайте, в админке и у Vadim.</p>
      </div>

      <Card className="bg-slate-800 border-slate-700">
        <CardHeader><CardTitle className="text-white text-lg flex items-center gap-2"><CalendarX className="h-5 w-5" />{editId ? "Изменить период" : "Новый блокированный период"}</CardTitle></CardHeader>
        <CardContent>
          <form onSubmit={add} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1"><Label className="text-slate-300">Дата начала</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value, end_date: form.end_date || e.target.value })} className="bg-slate-900 border-slate-600 text-white" data-testid="block-start" /></div>
            <div className="space-y-1"><Label className="text-slate-300">Дата окончания</Label><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="bg-slate-900 border-slate-600 text-white" data-testid="block-end" /></div>
            <label className="flex items-center gap-2 text-slate-300 text-sm sm:col-span-2"><input type="checkbox" checked={form.all_day} onChange={(e) => setForm({ ...form, all_day: e.target.checked })} className="h-4 w-4" data-testid="block-allday" />Весь день</label>
            {!form.all_day && (
              <>
                <div className="space-y-1"><Label className="text-slate-300">С (время)</Label><Input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} className="bg-slate-900 border-slate-600 text-white" data-testid="block-start-time" /></div>
                <div className="space-y-1"><Label className="text-slate-300">До (время)</Label><Input type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} className="bg-slate-900 border-slate-600 text-white" data-testid="block-end-time" /></div>
              </>
            )}
            <div className="space-y-1 sm:col-span-2"><Label className="text-slate-300">Причина</Label><Input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="напр. Отпуск, Больничный, Fellesferie (внутреннее)" className="bg-slate-900 border-slate-600 text-white" data-testid="block-reason" /></div>
            <div className="space-y-2 sm:col-span-2 rounded-md border border-slate-700 p-3" data-testid="block-notice-box">
              <Label className="text-slate-300">Объявление для клиентов (необязательно)</Label>
              <Textarea value={form.notice_ru} onChange={(e) => setForm({ ...form, notice_ru: e.target.value })} rows={2} placeholder="По-русски: Мастерская временно закрыта, приём машин невозможен…" className="bg-slate-900 border-slate-600 text-white" data-testid="block-notice-ru" />
              <Button type="button" disabled={translating || !form.notice_ru.trim()} onClick={translate} className="bg-slate-600 hover:bg-slate-500 h-7 text-xs" data-testid="block-notice-translate">{translating ? "…" : "Перевести на норвежский"}</Button>
              <Textarea value={form.notice_no} onChange={(e) => setForm({ ...form, notice_no: e.target.value })} rows={2} placeholder="Tekst på norsk – slik kunden ser den (kan redigeres)" className="bg-slate-900 border-slate-600 text-white" data-testid="block-notice-no" />
              <label className="flex items-center gap-2 text-slate-300 text-sm"><input type="checkbox" checked={form.show_on_website} onChange={(e) => setForm({ ...form, show_on_website: e.target.checked })} data-testid="block-show-website" />Показать на сайте (баннер до конца периода)</label>
              <label className="flex items-center gap-2 text-slate-300 text-sm"><input type="checkbox" checked={form.notify_ai} onChange={(e) => setForm({ ...form, notify_ai: e.target.checked })} data-testid="block-notify-ai" />Передать AI (Vadim сообщает клиентам)</label>
            </div>
            <div className="sm:col-span-2 flex gap-2"><Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="block-submit">{saving ? "Сохранение..." : editId ? "Сохранить" : "Добавить блокировку"}</Button>
              {editId && <Button type="button" variant="secondary" onClick={() => { setEditId(null); setForm(EMPTY); }} data-testid="block-edit-cancel">Отмена</Button>}</div>
          </form>
        </CardContent>
      </Card>

      <Card className="bg-slate-800 border-slate-700">
        <CardHeader><CardTitle className="text-white text-lg">Блокированные периоды</CardTitle></CardHeader>
        <CardContent>
          {periods.length === 0 ? <p className="text-slate-400 text-sm" data-testid="block-list-empty">Нет блокировок.</p> : (
            <div className="space-y-2" data-testid="block-list">
              {periods.map((p) => (
                <div key={p.id} className="flex items-center justify-between bg-slate-900 rounded-md px-3 py-2">
                  <div className="text-sm text-slate-200">
                    <span className="font-medium">{p.start_date}{p.end_date !== p.start_date ? ` – ${p.end_date}` : ""}</span>
                    <span className="text-slate-400">{p.all_day ? " · весь день" : ` · ${p.start_time}–${p.end_time}`}{p.reason ? ` · ${p.reason}` : ""}</span>
                    {p.notice_no && <div className="text-xs text-slate-300 mt-0.5">«{p.notice_no}» <span className="text-slate-500">{p.show_on_website ? "· на сайте " : ""}{p.notify_ai ? "· для AI" : ""}{!p.show_on_website && !p.notify_ai ? "· не опубликовано" : ""}</span></div>}
                  </div>
                  <div className="flex">
                    <Button variant="ghost" size="icon" onClick={() => { setEditId(p.id); setForm({ ...EMPTY, ...p }); window.scrollTo({ top: 0, behavior: "smooth" }); }} className="text-blue-300 hover:text-blue-200" data-testid={`block-edit-${p.id}`}><Pencil className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => remove(p.id)} className="text-red-400 hover:text-red-300" data-testid={`block-del-${p.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="bg-slate-800 border-slate-700">
        <CardHeader><CardTitle className="text-white text-lg">Сотрудники</CardTitle></CardHeader>
        <CardContent>
          {staff.length === 0 ? <p className="text-slate-400 text-sm">Нет сотрудников.</p> : (
            <div className="space-y-2">{staff.map((s) => (<div key={s.id} className="text-sm text-slate-200 bg-slate-900 rounded-md px-3 py-2">{s.name} · {s.role} · {s.active ? "активен" : "неактивен"}</div>))}</div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
